import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/FacebookPageAnalyticsOverview.graphql';
import { Query, QueryfacebookPageAnalyticsOverviewArgs } from '../../__generated__/globalTypes';

export const useFacebookPageAnalyticsOverviewQuery = (
  options?: QueryHookOptions<Query, QueryfacebookPageAnalyticsOverviewArgs>
) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryfacebookPageAnalyticsOverviewArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useFacebookPageAnalyticsOverviewLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryfacebookPageAnalyticsOverviewArgs>
): QueryResult<Query, QueryfacebookPageAnalyticsOverviewArgs> & {
  getFacebookPageAnalyticsOverview: LazyQueryExecFunction<Query, QueryfacebookPageAnalyticsOverviewArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getFacebookPageAnalyticsOverview, result] = useLazyQuery<Query, QueryfacebookPageAnalyticsOverviewArgs>(
    QUERY,
    {
      onError: (error) => {
        enqueueSnackbar(t(error.message), { variant: 'error' });
      },
      ...options
    }
  );

  return { getFacebookPageAnalyticsOverview, ...result };
};

export const useFacebookPageAnalyticsOverviewPromiseQuery = () => {
  const client = useApolloClient();

  const getFacebookPageAnalyticsOverview = useCallback(
    (options?: Omit<QueryOptions<QueryfacebookPageAnalyticsOverviewArgs, Query>, 'query'>) =>
      client.query<Query, QueryfacebookPageAnalyticsOverviewArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getFacebookPageAnalyticsOverview };
};
// Suspense Query Hooks
export const useFacebookPageAnalyticsOverviewSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QueryfacebookPageAnalyticsOverviewArgs>
) => {
  const result = useSuspenseQuery<Query, QueryfacebookPageAnalyticsOverviewArgs>(QUERY, {
    ...options
  });

  return result;
};
